import { html, nothing, PropertyValues, TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { keyed } from "lit/directives/keyed.js";
import { DodonaElement } from "@/components/meta/dodona_element";
import { I18n } from "@/i18n/i18n";
import { type HandinPhase, type HandinProgress, IDLE } from "@/handin_progress";

const PHASE_LABELS: Record<Exclude<HandinPhase, "idle">, string> = {
    submitting: "js.judging.step_submitting",
    queued: "js.judging.step_queued",
    running: "js.judging.step_running",
};

/** The text a screen reader reads for a message that may contain markup. */
function textOf(markup: string): string {
    const template = document.createElement("template");
    template.innerHTML = markup;
    return template.content.textContent.trim();
}

/**
 * The live regions of the hand-in card. Its one visually hidden polite live region speaks each new phase in
 * `progress`, which d-handin-card sets, the slow message and every message passed to `say`, such as the notice that a
 * submission has finished judging or, when the feedback region already has focus, the verdict. Otherwise the verdict
 * of a shown submission is not spoken here: it is part of the name of the feedback region. Each message passed to
 * `alert` gets a new alert element.
 *
 * The polite region exists from page load and is read when its text changes, so a message identical to the previous
 * polite one is not read again. A new alert element is read each time it is inserted. Both were measured with
 * VoiceOver.
 *
 * @element d-handin-announcer
 *
 * @prop {HandinProgress} progress - Where the hand-in is. d-handin-card sets it.
 */
@customElement("d-handin-announcer")
export class HandinAnnouncer extends DodonaElement {
    @property({ attribute: false })
    progress: HandinProgress = IDLE;

    @state() private politeText = "";
    @state() private assertiveMessage: { text: string } | null = null;

    /** Speaks `text` when the screen reader has finished what it is saying. */
    say(text: string): void {
        this.politeText = text;
    }

    /** Speaks `text` at once, interrupting other speech. Markup in `text` is not read. */
    alert(text: string): void {
        this.assertiveMessage = { text: textOf(text) };
    }

    // Going back to idle says nothing: the page speaks the result or the failure afterwards.
    willUpdate(changed: PropertyValues): void {
        if (!changed.has("progress")) return;
        const previous = (changed.get("progress") as HandinProgress | undefined) ?? IDLE;
        const { phase, slow } = this.progress;
        if (phase !== previous.phase && phase !== "idle") {
            this.say(I18n.t(PHASE_LABELS[phase]));
        }
        if (slow && !previous.slow) {
            this.say(I18n.t("js.judging.slow_message"));
        }
    }

    render(): TemplateResult {
        const message = this.assertiveMessage;
        const alert = message
            ? keyed(message, html`<div class="visually-hidden" role="alert" aria-atomic="true">${message.text}</div>`)
            : nothing;
        return html`
            <div class="visually-hidden" role="status" aria-atomic="true">${this.politeText}</div>
            ${alert}
        `;
    }
}
